import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LogOut, Menu, X } from "lucide-react";
import { debounce } from "lodash";
import { BASE_URL } from "@utils/constants";
import { setNotifications, setUnreadCount } from "@store/slices/dashboardSlice";
import kanthasthLogo from "@assets/kanthasth.svg";
import { logoutUser } from "@store/thunks/authAction";
import apiClient from "@/services/api/apiClient";


function getCapitalizedInitial(title) {
  const parts = title.split(" ");
  if (parts.length > 1) {
    return parts[1].charAt(0).toUpperCase();
  }
  return "";
}

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userId, token, user } = useSelector((store) => store.auth);
  const { notifications, unreadCount } = useSelector((state) => state.dashboard);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [notificationType, setNotificationType] = useState("all"); // all, assignment, rejection

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 150),
    []
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetSearchQuery(value);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/", { replace: true }); // instant redirect
  };

  // Filter notifications based on search query and type
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;
    if (notificationType === "assignment") {
      filtered = notifications.filter(notif => notif.type === "ASSIGNMENT");
    } else if (notificationType === "rejection") {
      filtered = notifications.filter(notif => notif.type === "REJECTED");
    }
    if (!searchQuery) return filtered;
    return filtered.filter(notif =>
    (notif.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notif.message?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [notifications, searchQuery, notificationType]);

  const handleOpenNotifications = async () => {
    console.log("Auth State on Open Notifications:", { isAuthenticated, });
    if (!isAuthenticated) return;
    setShowNotifications(true);
    setSearchQuery("");
    setInputValue("");
    setNotificationType("all");
    try {
      console.log("🔔 handleOpenNotifications called");
      const data = await apiClient(`${BASE_URL}api/notification/fetch?userId=${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: "include",
      }, navigate);
      console.log("Fetched notifications:", data);
      if (data.status && Array.isArray(data.data)) {
        dispatch(setNotifications(data.data));
        dispatch(setUnreadCount(data.data.filter((n) => !n.isRead).length));
      } else {
        console.error("Invalid notification data:", data);
        dispatch(setNotifications([]));
        dispatch(setUnreadCount(0));
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      dispatch(setNotifications([]));
      dispatch(setUnreadCount(0));
    }
  };

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    setSearchQuery("");
    setInputValue("");
    setNotificationType("all");
  };

  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);

    // Do nothing if the notification is already read
    if (!notification || notification.isRead) {
      return;
    }

    // Optimistic UI Update: Update the state immediately
    const originalNotifications = [...notifications];
    const updatedNotifications = notifications.map(n =>
      n.id === notificationId ? { ...n, isRead: true } : n
    );
    dispatch(setNotifications(updatedNotifications));
    dispatch(setUnreadCount(unreadCount - 1));

    try {
      // API Call to update the backend
      const response = await apiClient(`${BASE_URL}api/notification/read?notificationId=${notificationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`
        },
        credentials: "include",
      }, navigate);

    } catch (error) {
      console.error('Error marking notification as read:', error);
      // If the API call fails, revert the UI to its original state
      dispatch(setNotifications(originalNotifications));
      dispatch(setUnreadCount(unreadCount));
      // Optionally, show an error message to the user here
    }
  };

  if (location.pathname === "/" || location.pathname === "/login") {
    return null;
  }


  return (
    <header className="fixed top-0 w-full bg-gradient-to-r from-gray-50 via-white to-gray-50 shadow-md z-50 mb-4 ">
      <div className="flex items-center justify-between h-16 px-0">
        {/* Left: Logo & Title */}
        <div className="flex items-center space-x-4 pl-4">
          <img src={kanthasthLogo} alt="Kanthasth Logo" className="h-12 w-auto" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-wide bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text animate-gradient-text">
            Bharati Sangraha
          </h1>
        </div>

        {/* Right: Desktop Logout */}
        {/* Right: Desktop Actions */}
        <div className="hidden sm:flex items-center pr-4 gap-3 relative">
          {/* My Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowProfileDropdown((prev) => !prev)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 mr-1.5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.121 17.804A13.937 13.937 0 0112 15c2.485 0 4.847.607 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>My Profile</span>
              <svg
                className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${showProfileDropdown ? "rotate-180" : ""
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 animate-fadeIn">
                <h1 className="px-4 py-2 text-sm font-semibold text-gray-800">
                  <span>User logged in: </span><span className="text-[#ec7026] text-lg">{user}</span>
                </h1>
              </div>
            )}

          </div>

          {/* Notifications Button */}
          <div className="relative">
            <button
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              onClick={handleOpenNotifications}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ec7026] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-[#ec7026] text-white text-xs items-center justify-center font-['Semantic/Strong']">
                    {unreadCount}
                  </span>
                </span>
              )}
            </button>
          </div>
          {showNotifications && (
            <div
              className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
              onClick={handleCloseNotifications} // Close when clicking outside
            >
              <div
                className="w-[28rem] bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 animate-fadeIn max-h-[75vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside
              >

                {/* Header */}
                <div className="flex items-center justify-between px-2 pb-3 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800 tracking-wide">
                    🔔 Notifications
                  </h2>
                  <button
                    onClick={handleCloseNotifications}
                    className="text-gray-500 hover:text-gray-800 text-lg transition-all"
                  >
                    ✕
                  </button>
                </div>

                {/* Filter Tabs */}
                <div className="flex justify-center gap-3 py-3 border-b border-gray-100">
                  {["all", "assignment", "rejection"].map((type) => (
                    <button
                      key={type}
                      onClick={() => setNotificationType(type)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize shadow-sm transition-all duration-200 ${notificationType === type
                        ? "bg-[#ec7026] text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {/* Search Input */}
                <div className="px-3 py-3">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="🔍  Search notifications..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ec7026] focus:border-[#ec7026] outline-none transition-all"
                  />
                </div>

                {/* Notification List */}
                <ul className="px-2 space-y-3 mt-1">
                  {filteredNotifications.length > 0 ? (
                    filteredNotifications.map((notif) => (
                      <li
                        key={notif.id}
                        onClick={() => handleMarkAsRead(notif.id)}
                        className={`p-4 rounded-xl border border-gray-200 cursor-pointer transition-all duration-200 ${notif.isRead
                          ? "bg-white hover:bg-gray-50"
                          : "bg-orange-50 hover:bg-orange-100"
                          }`}
                      >
                        <p className="font-semibold text-gray-900 text-base mb-1">
                          {notif.title}
                        </p>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {notif.message}
                        </p>
                        <p className="text-gray-400 text-xs mt-2 italic">
                          {new Date(notif.sentAt).toLocaleString()}
                        </p>
                      </li>
                    ))
                  ) : (
                    <li className="text-center text-gray-500 text-sm py-6">
                      No notifications available
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}




          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="group flex items-center gap-2 px-4 py-1.5 border border-red-600 text-red-600 rounded-md text-sm font-medium transition-all duration-200 hover:bg-red-600 hover:text-white hover:shadow-sm"
          >
            <LogOut className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            <span>Logout</span>
          </button>
        </div>


        {/* Right: Mobile Menu Toggle */}
        <div className="sm:hidden pr-4">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-gray-700 hover:text-blue-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Logout Menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-gray-50 border-t border-gray-200">
          <div className="px-4 py-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-4 py-2 border border-red-600 text-red-600 rounded-md text-sm font-medium transition-all duration-200 hover:bg-red-600 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;